/** Airport label helpers: City (Country) + official name only. */

import type { AirportInfo } from '../types/flight';

const PAREN_TAIL = /\s*\(([^)]*)\)\s*$/;

function pickLang(
  useRu: boolean,
  ru: string | null | undefined,
  en: string | null | undefined,
): string {
  const a = (useRu ? ru || en : en || ru)?.trim() || '';
  return a;
}

function peelParen(value: string): { base: string; paren: string | null } {
  const raw = value.trim();
  const m = PAREN_TAIL.exec(raw);
  if (!m) return { base: raw, paren: null };
  return { base: raw.slice(0, m.index).trim(), paren: (m[1] || '').trim() || null };
}

/** Parse legacy "Name, City (Country)" / polluted blobs into parts. */
export function parseAirportDisplayBlob(raw: string | null | undefined): {
  name: string;
  city: string;
  country: string;
} {
  const s = (raw || '').trim();
  if (!s) return { name: '', city: '', country: '' };
  if (s.includes(',')) {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    const left = parts[0] || '';
    const right = parts[parts.length - 1] || '';
    const { base, paren } = peelParen(right);
    return { name: left, city: base || right, country: paren || '' };
  }
  const { base, paren } = peelParen(s);
  if (paren) return { name: '', city: base, country: paren };
  return { name: s, city: '', country: '' };
}

export function cityCountryLabel(
  info: AirportInfo | null | undefined,
  cityField: string,
  useRu: boolean,
): string {
  let city = pickLang(useRu, info?.city_ru, info?.city_en);
  let country = pickLang(useRu, info?.country_ru, info?.country_en);

  if (!city || !country) {
    const parsed = parseAirportDisplayBlob(cityField);
    city = city || parsed.city;
    country = country || parsed.country;
  }

  // Drop district-like paren from city when country is known separately.
  if (city) {
    const { base, paren } = peelParen(city);
    if (paren && country && paren.toLowerCase() !== country.toLowerCase()) {
      city = base;
    } else if (paren && !country) {
      // "Чэнду (Китай)" already ok as city+country
      city = base;
      country = paren;
    } else if (paren) {
      city = base;
    }
  }

  if (city && country) return `${city} (${country})`;
  return city || country || '';
}

export function airportNameLabel(
  info: AirportInfo | null | undefined,
  cityField: string,
  useRu: boolean,
): string {
  let name = pickLang(useRu, info?.name_ru, info?.name_en);
  if (!name) {
    const parsed = parseAirportDisplayBlob(cityField);
    name = parsed.name;
  }
  // Strip trailing ", City (Country)" if somehow still glued.
  if (name.includes(',')) {
    name = name.split(',')[0]?.trim() || name;
  }
  const { base, paren } = peelParen(name);
  const country = pickLang(useRu, info?.country_ru, info?.country_en);
  if (paren && country && paren.toLowerCase() === country.toLowerCase()) {
    name = base;
  }
  return name;
}

/** Full title for tooltips / airport-info: Name, City (Country). */
export function fullAirportTitle(
  info: AirportInfo | null | undefined,
  cityField: string,
  useRu: boolean,
): string {
  const name = airportNameLabel(info, cityField, useRu);
  const cityCountry = cityCountryLabel(info, cityField, useRu);
  if (name && cityCountry) return `${name}, ${cityCountry}`;
  return name || cityCountry || (cityField || '').trim();
}
